/**
 * ScenarioImport Component
 * Allows users to import test scenarios from Excel file
 */

import React, { useState } from 'react';
import { apiClient, TestScenario } from '../services/api';
import './ScenarioImport.css';

interface ScenarioImportProps {
  folderId: string;
  onImportComplete?: (scenarios: TestScenario[]) => void;
  onCancel?: () => void;
}

export const ScenarioImport: React.FC<ScenarioImportProps> = ({
  folderId,
  onImportComplete,
  onCancel,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importedScenarios, setImportedScenarios] = useState<TestScenario[]>([]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Validate file type
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        setError('Please select an Excel file (.xlsx or .xls)');
        return;
      }
      
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await apiClient.downloadScenarioTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'scenario_import_template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download template');
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      setError('Please select a file to import');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const scenarios = await apiClient.importScenariosFromExcel(folderId, selectedFile);
      setImportedScenarios(scenarios);
      onImportComplete?.(scenarios);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import scenarios');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setError(null);
    setImportedScenarios([]);
  };

  return (
    <div className="scenario-import">
      <div className="import-header">
        <h2>Import Test Scenarios from Excel</h2>
        <p>Upload an Excel file to import multiple test scenarios at once</p>
      </div>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      {importedScenarios.length > 0 ? (
        <div className="import-success">
          <div className="success-message">
            ✅ Successfully imported {importedScenarios.length} scenario(s)!
          </div>
          
          <div className="imported-scenarios-list">
            <h3>Imported Scenarios:</h3>
            {importedScenarios.map((scenario, index) => (
              <div key={scenario.id} className="imported-scenario-item">
                <span className="scenario-number">{index + 1}.</span>
                <div className="scenario-info">
                  <div className="scenario-description">
                    {scenario.scenario_description}
                  </div>
                  <div className="scenario-meta">
                    <span className={`priority-badge priority-${scenario.priority.toLowerCase()}`}>
                      {scenario.priority}
                    </span>
                    {scenario.module_name && (
                      <span className="module-badge">{scenario.module_name}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="import-actions">
            <button
              className="btn btn-primary"
              onClick={handleReset}
            >
              Import More
            </button>
            {onCancel && (
              <button
                className="btn btn-secondary"
                onClick={onCancel}
              >
                Done
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="import-instructions">
            <h3>Instructions:</h3>
            <ol>
              <li>Download the Excel template</li>
              <li>Fill in your test scenarios following the template format</li>
              <li>Upload the completed Excel file</li>
              <li>Click "Import" to add scenarios to this project</li>
            </ol>

            <div className="template-info">
              <h4>Required Columns:</h4>
              <ul>
                <li><strong>scenario_description</strong> - Description of the test scenario</li>
                <li><strong>expected_result</strong> - Expected outcome</li>
                <li><strong>priority</strong> - Critical, High, Medium, or Low</li>
              </ul>

              <h4>Optional Columns:</h4>
              <ul>
                <li><strong>preconditions</strong> - Prerequisites for the test</li>
                <li><strong>module_name</strong> - Feature or module name</li>
                <li><strong>tags</strong> - Comma-separated tags</li>
              </ul>
            </div>
          </div>

          <div className="import-actions">
            <button
              className="btn btn-secondary"
              onClick={handleDownloadTemplate}
              disabled={isLoading}
            >
              📥 Download Template
            </button>
          </div>

          <div className="file-upload-section">
            <h3>Upload Excel File:</h3>
            <div className="file-input-wrapper">
              <input
                type="file"
                id="excel-file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                disabled={isLoading}
              />
              <label htmlFor="excel-file" className="file-input-label">
                {selectedFile ? selectedFile.name : 'Choose Excel file...'}
              </label>
            </div>

            {selectedFile && (
              <div className="file-info">
                <span>✓ File selected: {selectedFile.name}</span>
                <span>Size: {(selectedFile.size / 1024).toFixed(2)} KB</span>
              </div>
            )}
          </div>

          <div className="import-actions">
            <button
              className="btn btn-primary btn-lg"
              onClick={handleImport}
              disabled={isLoading || !selectedFile}
            >
              {isLoading ? 'Importing...' : 'Import Scenarios'}
            </button>
            {onCancel && (
              <button
                className="btn btn-secondary"
                onClick={onCancel}
                disabled={isLoading}
              >
                Cancel
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};
